import React from "react";
import { Button, Card, Modal, message } from "antd";
import { PreviewData } from "../types";
import { createStorageInvoice } from "~/services/quickbooks/quickbooks.api";

interface InvoicePreviewModalProps {
  visible: boolean;
  onClose: () => void;
  previewData: PreviewData | null;
  onSuccess: () => void;
}

export const InvoicePreviewModal: React.FC<InvoicePreviewModalProps> = ({
  visible,
  onClose,
  previewData,
  onSuccess,
}) => {
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handleCreateInvoice = async () => {
    try {
      setIsProcessing(true);

      if (!previewData) {
        message.error("Preview data missing");
        return;
      }

      // Create storage invoice
      const response = await createStorageInvoice({
        clientId: previewData.client.quickBooksId,
        month: previewData.monthName,
        year: previewData.year,
        averageCubicFeet: previewData.calculation.averageCubicFootage,
        cubicFootRate: previewData.calculation.storageRate,
        includeInsurance: previewData.calculation.includeInsurance,
        insuranceRate: previewData.calculation.insuranceRate || undefined,
        inventoryValue: previewData.calculation.totalInventoryValue,
      });

      message.success(
        `Invoice ${response.Invoice?.Id || ""} created successfully`
      );
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error creating storage invoice:", error);
      message.error("Failed to create storage invoice");
    } finally {
      setIsProcessing(false);
    }
  };

  if (!previewData) return null;

  return (
    <Modal
      title={`Storage Invoice Preview - ${previewData.client.companyName}`}
      open={visible}
      width={700}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleCreateInvoice}
          loading={isProcessing}
        >
          Create Invoice
        </Button>,
      ]}
    >
      <Card className="mb-4">
        <div className="flex justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium">
              Invoice for {previewData.client.companyName}
            </h3>
            <p>
              Storage Period: {previewData.monthName} {previewData.year}
            </p>
            <p>Date: {new Date().toLocaleDateString()}</p>
          </div>
          <div className="text-right">
            <p>
              <strong>Art Handler Company</strong>
            </p>
            <p>123 Art St, New York, NY</p>
            <p>info@arthandlercompany.com</p>
          </div>
        </div>

        <table className="w-full text-sm mt-6">
          <thead>
            <tr className="border-b border-t">
              <th className="py-2 text-left">Description</th>
              <th className="py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="py-2">
                Monthly Storage - {previewData.monthName} {previewData.year} -{" "}
                {previewData.calculation.averageCubicFootage.toFixed(2)} cubic
                feet @ ${previewData.calculation.storageRate.toFixed(2)}/cubic
                foot
                <br />
                <span className="text-xs text-gray-500">
                  (Containers and standalone pieces only, contents not
                  double-counted)
                </span>
              </td>
              <td className="py-2 text-right">
                ${previewData.storageAmount.toFixed(2)}
              </td>
            </tr>

            {previewData.calculation.includeInsurance &&
              previewData.calculation.insuranceRate && (
                <tr className="border-b">
                  <td className="py-2">
                    Insurance (
                    {(previewData.calculation.insuranceRate * 100).toFixed(1)}
                    % of $
                    {previewData.calculation.totalInventoryValue.toFixed(2)})
                  </td>
                  <td className="py-2 text-right">
                    ${previewData.insuranceAmount.toFixed(2)}
                  </td>
                </tr>
              )}

            <tr className="border-b">
              <td className="py-2 font-medium">Subtotal</td>
              <td className="py-2 text-right font-medium">
                ${previewData.subtotal.toFixed(2)}
              </td>
            </tr>

            <tr className="border-b">
              <td className="py-2">Tax (8.875%)</td>
              <td className="py-2 text-right">
                ${previewData.taxAmount.toFixed(2)}
              </td>
            </tr>

            <tr className="font-medium text-lg">
              <td className="py-2">Total</td>
              <td className="py-2 text-right">
                ${previewData.totalAmount.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </Card>

      <Card title="Storage Calculation Details">
        <p>
          <strong>Calculation Period:</strong> Last 5 days of{" "}
          {previewData.monthName}
        </p>
        <p className="text-sm text-gray-600 mt-2">
          <strong>Note:</strong> Calculation includes cubic footage of
          containers and standalone pieces only. Items contained within
          containers are noted but their individual cubic footage is not
          included to avoid double-counting. Storage rates are calculated per
          location based on inventory placement.
        </p>

        <table className="w-full text-sm mt-3">
          <thead>
            <tr className="border-b">
              <th className="text-left">Date</th>
              <th className="text-right">Cubic Footage</th>
            </tr>
          </thead>
          <tbody>
            {previewData.calculation.snapshots.map((snapshot) => (
              <tr key={snapshot.date} className="border-b">
                <td>{snapshot.date}</td>
                <td className="text-right">
                  {snapshot.cubicFootage.toFixed(2)}
                </td>
              </tr>
            ))}
            <tr className="font-medium">
              <td>Average:</td>
              <td className="text-right">
                {previewData.calculation.averageCubicFootage.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </Card>
    </Modal>
  );
}; 